import { useTranslation } from "react-i18next";

import ShortTextIcon from "@mui/icons-material/ShortText";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import TabPanel, { TabPanelProps } from "@mui/lab/TabPanel";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  ListSubheaderProps,
  Paper,
  Typography,
} from "@mui/material";

import { PatientData } from "../models/patient-data";
import { DateTime } from "./primitives/DateTime";
import React, { useMemo } from "react";
import { HorizontalStack } from "./primitives/Stacks";
import { Badge } from "./primitives/Badge";

const listSubheaderProps: ListSubheaderProps = {
  sx: {
    bgcolor: "background.default",
    borderBottom: "0.75px solid",
    borderColor: "divider",
    color: "text.primary",
    fontWeight: 500,
  },
};

export const NotesTab: React.FC<TabPanelProps & { data: PatientData }> = ({
  data: { data },
  ...tabPanelProps
}) => {
  const { t } = useTranslation();

  const hasNotes = data?.notes?.length > 0;

  const sortedNotes = useMemo(() => {
    return data.notes?.sort((a, b) => b.timestamp - a.timestamp) || [];
  }, [data]);

  return (
    <TabPanel {...tabPanelProps}>
      <Grid container spacing={{ xs: 2, md: 4 }}>
        <Grid xs={12} order={hasNotes ? 1 : 2}>
          <Paper sx={{ overflow: "hidden" }} variant="outlined">
            <List
              sx={{ maxHeight: 400, overflow: "auto", scrollbarWidth: "thin" }}
              dense
              disablePadding
              subheader={
                <ListSubheader {...listSubheaderProps}>
                  <HorizontalStack alignItems="center" spacing={1}>
                    <span>{t("tabs.more.notes-label")}</span>
                    <Badge
                      label={data?.notes?.length || 0}
                      size="small"
                      variant="outlined"
                    />
                  </HorizontalStack>
                </ListSubheader>
              }
            >
              {hasNotes ? (
                sortedNotes.map((note, num) => (
                  <ListItem
                    key={note.id}
                    divider={num < data?.notes?.length - 1}
                  >
                    <ListItemIcon>
                      <ShortTextIcon
                        fontSize="small"
                        sx={{ color: "text.disabled" }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Grid
                          alignItems="center"
                          container
                          spacing={{ xs: 0.5, md: 2 }}
                        >
                          <Grid xs={12} md>
                            <Typography variant="body2">{note.note}</Typography>
                          </Grid>
                          <Grid xs={12} md="auto">
                            <Typography
                              alignItems="center"
                              color="text.secondary"
                              display="flex"
                              sx={({ spacing }) => ({ gap: spacing(0.5) })}
                              variant="caption"
                            >
                              <DateTime timestamp={note.timestamp} />
                            </Typography>
                          </Grid>
                        </Grid>
                      }
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText
                    primary={t("tabs.more.notes-no-data")}
                    primaryTypographyProps={{
                      align: "center",
                      color: "text.secondary",
                    }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </TabPanel>
  );
};

export default NotesTab;
